import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';

import { catchError, forkJoin, map, Observable, of } from 'rxjs';

import { TargetPage } from '../enums/target-page.enum';

import { Address } from '../models/address.model';
import { URL_DATE_FORMAT, URL_TIME_FORMAT } from '../models/date-time-format.model';

import { AccountService } from '../services/account.service';
import { AddressService } from '../services/address.service';
import { AreasService } from '../services/areas.service';
import { DateTimeService } from '../services/date-time.service';
import { DeepLinkService } from '../services/deep-link.service';
import { NavigationService } from '../services/navigation.service';
import { PushNotificationsService } from '../services/push-notifications.service';
import { SettingsService } from '../services/settings.service';
import { UniversalLinksService } from '../services/universal-links.service';
import { VendorsService } from '../services/vendors.service';

@Injectable({
  providedIn: 'root',
})
export class HomePageVendorsGuard implements CanActivate {
  constructor(
    private router: Router,
    private areasService: AreasService,
    private addressService: AddressService,
    private accountService: AccountService,
    private vendorsService: VendorsService,
    private dateTimeService: DateTimeService,
    private deepLinkService: DeepLinkService,
    private settingsService: SettingsService,
    private navigationService: NavigationService,
    private universalLinksService: UniversalLinksService,
    private pushNotificationsService: PushNotificationsService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot) {
    const currentFragment = route.fragment;
    const currentQueryParams = route.queryParams || {};
    const urlSegments = [this.navigationService.getCountryLanguageUrlSegment(), 'vendors', 'search'];

    // This guard checks the fragment and the query string params one by one. So for example
    // if the service type AND the area needs to be fixed, the guard will fix the service type
    // first and then it will be called again to fix the area.

    const { shouldUpdateUrl: shouldUpdateServiceTypeFromUrl, newValue: newServiceType } = this.checkServiceType(route);

    if (shouldUpdateServiceTypeFromUrl && newServiceType) {
      return this.router.createUrlTree(urlSegments, {
        fragment: newServiceType.toString(),
        queryParams: currentQueryParams,
      });
    }

    // If we reached this point, we can assume the service type
    // from the URL was correct or was already fixed otherwise
    const updatedServiceType = currentFragment ? +currentFragment : undefined;

    const { shouldUpdateUrl: shouldUpdateDateTimeFromUrl, newDateValue, newTimeValue } = this.checkDateTime(route, updatedServiceType);

    if (shouldUpdateDateTimeFromUrl) {
      return this.router.createUrlTree(urlSegments, {
        fragment: currentFragment,
        queryParams: {
          ...currentQueryParams,
          date: newDateValue ?? undefined,
          time: newTimeValue ?? undefined,
        },
      });
    }

    return this.checkAreaId(route).pipe(
      map((areaCheckResult) => {
        const { shouldUpdateUrl: shouldUpdateAreaIdFromUrl, newValue: newAreaId } = areaCheckResult;

        if (shouldUpdateAreaIdFromUrl) {
          return this.router.createUrlTree(urlSegments, {
            fragment: currentFragment,
            queryParams: { ...currentQueryParams, areaId: newAreaId },
          });
        }

        return true;
      }),
    );
  }

  private checkServiceType(route: ActivatedRouteSnapshot): { shouldUpdateUrl: boolean; newValue?: number } {
    const serviceTypeFromUrl = this.navigationService.getServiceTypeFromUrl(route);

    if (serviceTypeFromUrl && this.settingsService.isServiceTypeOptionValid(serviceTypeFromUrl)) {
      return { shouldUpdateUrl: false };
    }

    const deepLinkServiceType = this.getServiceTypeFromPendingDeepLinks();

    if (deepLinkServiceType) {
      return { shouldUpdateUrl: true, newValue: deepLinkServiceType };
    }

    return { shouldUpdateUrl: true, newValue: this.settingsService.getServiceTypeOptions()[0].serviceTypes };
  }

  private checkDateTime(
    route: ActivatedRouteSnapshot,
    serviceType: number,
  ): { shouldUpdateUrl: boolean; newDateValue?: string; newTimeValue?: string } {
    const dateFromUrl = this.navigationService.getDateFromUrl(route);
    const timeFromUrl = this.navigationService.getTimeFromUrl(route);

    const { dateTime: dateTimeFromSearchFilters, orderForNow: orderForNowFromSearchFilters } =
      this.vendorsService.getDatetimeSearchFilters(serviceType);
    const { date: newDateFromSearchFilters, time: newtimeFromSearchFilters } =
      this.navigationService.getDateAndTimeQueryParamsFromIsoString(dateTimeFromSearchFilters, orderForNowFromSearchFilters);

    if (!dateFromUrl && !timeFromUrl) {
      return {
        shouldUpdateUrl: !!newDateFromSearchFilters && !!newtimeFromSearchFilters,
        newDateValue: newDateFromSearchFilters || undefined,
        newTimeValue: newtimeFromSearchFilters || undefined,
      };
    }

    if (!dateFromUrl || !timeFromUrl) {
      return {
        shouldUpdateUrl: true,
        newDateValue: newDateFromSearchFilters || undefined,
        newTimeValue: newtimeFromSearchFilters || undefined,
      };
    }

    const isDateFormatInvalid = !this.dateTimeService.isValid(dateFromUrl, URL_DATE_FORMAT);
    const isTimeFormatInvalid = timeFromUrl !== 'now' && !this.dateTimeService.isValid(timeFromUrl, URL_TIME_FORMAT);

    if (isDateFormatInvalid || isTimeFormatInvalid) {
      return {
        shouldUpdateUrl: true,
        newDateValue: newDateFromSearchFilters || undefined,
        newTimeValue: newtimeFromSearchFilters || undefined,
      };
    }

    if (timeFromUrl === 'now') {
      const dateIsToday = this.dateTimeService.isToday(this.dateTimeService.convertToDateTimeIsoString(dateFromUrl, URL_DATE_FORMAT));
      if (!dateIsToday) {
        return {
          shouldUpdateUrl: true,
          newDateValue: this.dateTimeService.now.format(URL_DATE_FORMAT),
          newTimeValue: 'now',
        };
      }
    }

    if (timeFromUrl !== 'now') {
      const isDateTimeBeforeCurrentTime = this.dateTimeService.isBeforeCurrentLocalTime(
        this.dateTimeService.convertToDateTimeIsoString(`${dateFromUrl}${timeFromUrl}`, `${URL_DATE_FORMAT}${URL_TIME_FORMAT}`),
      );

      if (isDateTimeBeforeCurrentTime) {
        return {
          shouldUpdateUrl: true,
          newDateValue: newDateFromSearchFilters || undefined,
          newTimeValue: newtimeFromSearchFilters || undefined,
        };
      }
    }

    if (!serviceType) {
      // If the service type is empty, the app will select the
      // "shortcuts" section which means the date/time should
      // be removed but WITHOUT clearing the search filters
      return { shouldUpdateUrl: true, newDateValue: undefined, newTimeValue: undefined };
    }

    const { dateTime, orderForNow } = this.navigationService.getDateTimeAndOrderForNowFromUrl(route);
    this.vendorsService.setDateTimeSearchFilters(serviceType, { dateTime, orderForNow });
    return { shouldUpdateUrl: false };
  }

  private checkAreaId(route: ActivatedRouteSnapshot): Observable<{ shouldUpdateUrl: boolean; newValue?: number }> {
    const areaIdFromUrl = this.navigationService.getAreaIdFromUrl(route);
    const addressFromSearchFilters = this.vendorsService.getAddressSearchFilters();

    if (!areaIdFromUrl) {
      return of({
        shouldUpdateUrl: !!addressFromSearchFilters,
        newValue: addressFromSearchFilters ? addressFromSearchFilters.area.areaId : undefined,
      });
    }

    if (addressFromSearchFilters && addressFromSearchFilters.area.areaId === areaIdFromUrl) {
      return of({ shouldUpdateUrl: false });
    }

    return forkJoin([
      this.areasService.getAreaById(areaIdFromUrl).pipe(catchError(() => of(undefined))),
      this.accountService.isLoggedIn()
        ? this.accountService.getUserSavedAddresses(true).pipe(catchError(() => of([] as Array<Address>)))
        : of([] as Array<Address>),
    ]).pipe(
      map(([area, savedAddresses]) => {
        if (savedAddresses && savedAddresses.length) {
          const existingAddress = savedAddresses.find((address) => address.area.areaId === areaIdFromUrl);

          if (existingAddress) {
            this.vendorsService.setAddressSearchFilters(existingAddress);
            return { shouldUpdateUrl: false };
          }
        }

        if (area) {
          this.vendorsService.setAddressSearchFilters(this.addressService.createNewAddressFromArea(area));
          return { shouldUpdateUrl: false };
        }

        // The areaID specified in the URL doesn't exist, so we should
        // use the search filters or just remove it from the URL
        return {
          shouldUpdateUrl: true,
          newValue: addressFromSearchFilters ? addressFromSearchFilters.area.areaId : undefined,
        };
      }),
    );
  }

  private getServiceTypeFromPendingDeepLinks(): number {
    const pendingUniversalLink = this.universalLinksService.getPendingUniversalLinkToBeHandledInSearchResultsPage();
    const serviceTypeFromUniversalLink = pendingUniversalLink
      ? pendingUniversalLink.targetPage === TargetPage.ServiceType
        ? pendingUniversalLink.serviceTypes
        : pendingUniversalLink.targetPage === TargetPage.TopSearchedServiceType
        ? this.deepLinkService.getTopSearchedServiceType()
        : undefined
      : undefined;

    if (serviceTypeFromUniversalLink && this.settingsService.isServiceTypeValid(serviceTypeFromUniversalLink)) {
      return serviceTypeFromUniversalLink;
    }

    const pendingPushNotification = this.pushNotificationsService.getPendingNotificationToBeHandledInSearchResultsPage();
    const serviceTypeFromPushNotification = pendingPushNotification
      ? pendingPushNotification.type === TargetPage.ServiceType
        ? pendingPushNotification.serviceTypes
        : pendingPushNotification.type === TargetPage.TopSearchedServiceType
        ? this.deepLinkService.getTopSearchedServiceType()
        : undefined
      : undefined;

    if (serviceTypeFromPushNotification && this.settingsService.isServiceTypeValid(serviceTypeFromPushNotification)) {
      return serviceTypeFromPushNotification;
    }
  }
}
